/**
 * The 7 ui_category buckets and the category-based colour rule.
 *
 * Colour rule: colour encodes CATEGORY, never severity.
 *  - road_closure → red disc + white glyph
 *  - everything else → yellow disc + black glyph
 * The glyph SHAPE is what tells categories apart; colour is a coarse signal.
 */

export const CATEGORIES = {
  weather_alerts: { key: "weather_alerts", label: "Weather" },
  civil_disruption: { key: "civil_disruption", label: "Civil Disruption" },
  road_closure: { key: "road_closure", label: "Road Closure" },
  road_congestion: { key: "road_congestion", label: "Road Disruption" },
  political_disturbance: { key: "political_disturbance", label: "Political / VIP" },
  religious_cultural: { key: "religious_cultural", label: "Religious / Cultural" },
  hazard: { key: "hazard", label: "Hazard / Utility" },
};

// Stable display order for the legend and the filter list.
export const CATEGORY_ORDER = [
  "road_closure",
  "road_congestion",
  "weather_alerts",
  "civil_disruption",
  "political_disturbance",
  "religious_cultural",
  "hazard",
];

const RED = "#D7263D";
const YELLOW = "#F4C20D";

export function categoryLabel(uiCategory) {
  return CATEGORIES[uiCategory]?.label || "Other";
}

/** Disc background + glyph foreground for a category. */
export function iconStyle(uiCategory) {
  if (uiCategory === "road_closure") return { bg: RED, fg: "#FFFFFF" };
  return { bg: YELLOW, fg: "#121212" };
}

/** The single colour used for an event's geometry (line/polygon/highlight base). */
export function iconColor(uiCategory) {
  return uiCategory === "road_closure" ? RED : YELLOW;
}

/**
 * Map the feed's free-text `event_type` (with `event_subtype` as a tie-breaker)
 * onto one of the 7 ui_category buckets. The feed uses human labels like
 * "Flood / Waterlogging" or "Political / VIP Movement" that don't match the
 * ui_category keys directly, so normalise here.
 */
export function uiCategoryOf(eventType, eventSubtype) {
  const type = String(eventType || "").toLowerCase();
  const subtype = String(eventSubtype || "").toLowerCase();
  const hay = `${type} ${subtype}`;

  // Explicit road closure wins over generic road disruption.
  if (/closure|closed|blocked|block|cave-?in|sinkhole/.test(hay)) {
    if (/road|highway|bridge|corridor|lane/.test(hay)) return "road_closure";
  }

  if (/flood|waterlog|rain|weather|storm|cyclone|cloudburst|snow|fog|heat/.test(hay)) {
    return "weather_alerts";
  }
  if (/landslide|rockslide|geological|earthquake|subsidence/.test(hay)) {
    return "hazard";
  }
  if (/religious|cultural|yatra|mela|festival|procession|pilgrim/.test(hay)) {
    return "religious_cultural";
  }
  if (/political|vip|rally|bandh|strike/.test(hay)) {
    return "political_disturbance";
  }
  if (/civil|protest|dharna|agitation|riot|unrest|clash/.test(hay)) {
    return "civil_disruption";
  }
  if (/road|highway|traffic|congestion|diversion|accident|jam/.test(hay)) {
    return "road_congestion";
  }
  return "hazard";
}
